<template>
  <section id="projects" class="py-16 sm:py-24 bg-white dark:bg-gray-900 relative overflow-hidden transition-colors duration-500">
    <div class="max-w-5xl mx-auto px-6 relative z-10">
      <!-- Section header -->
      <div class="mb-10 sm:mb-12">
        <span class="inline-block px-3 py-1 bg-amber-100/80 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 font-semibold text-xs rounded-full mb-3">{{ t('aiAssistant') }}</span>
        <h2 class="text-2xl sm:text-3xl md:text-4xl font-extrabold text-gray-900 dark:text-white tracking-tight mb-3">
          {{ t('chatWithMy') }} <span class="gradient-text">{{ t('aiTwin') }}</span>
        </h2>
        <p class="text-base text-gray-600 dark:text-gray-400 max-w-xl">
          {{ t('chatSubtitle') }}
        </p>
      </div>

      <!-- Main Chat Panel -->
      <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-lg shadow-gray-200/50 dark:shadow-black/20 border border-gray-200 dark:border-gray-700 overflow-hidden">

        <!-- Chat Header -->
        <div class="px-4 sm:px-6 py-3 sm:py-4 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-3">
              <div class="w-9 h-9 bg-amber-100 dark:bg-amber-900/30 rounded-lg flex items-center justify-center">
                <svg class="w-5 h-5 text-amber-700 dark:text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"/>
                </svg>
              </div>
              <div>
                <h3 class="text-sm font-semibold text-gray-900 dark:text-white">{{ t('paulosAiAssistant') }}</h3>
                <p class="text-xs text-gray-500 dark:text-gray-400">{{ t('poweredBy') }}</p>
              </div>
            </div>
            <div class="flex items-center gap-1.5 px-2.5 py-1 bg-emerald-50 dark:bg-emerald-900/20 rounded-full">
              <span class="relative flex h-2 w-2">
                <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                <span class="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
              </span>
              <span class="text-xs font-medium text-emerald-700 dark:text-emerald-400">{{ t('online') }}</span>
            </div>
          </div>
        </div>

        <!-- Chat Body -->
        <div
          ref="messagesContainer"
          class="p-4 sm:p-6 h-[350px] sm:h-[400px] overflow-y-auto space-y-4 bg-white dark:bg-gray-800"
        >
          <div
            v-for="(msg, idx) in messages"
            :key="idx"
            class="flex"
            :class="{ 'justify-end': msg.sender === 'user', 'hidden': msg.sender === 'bot' && !msg.text }"
          >
            <div
              class="max-w-[75%] px-5 py-3.5 rounded-2xl text-sm leading-relaxed"
              :class="msg.sender === 'user'
                ? 'bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 rounded-br-sm'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-bl-sm'"
            >
              <span v-if="msg.sender === 'user'">{{ msg.text }}</span>
              <span v-else class="chat-message">
                <span v-html="parseMarkdown(msg.text)"></span>
                <span v-if="msg.isStreaming" class="inline-block w-2 h-4 bg-amber-500 animate-pulse ml-0.5"></span>
              </span>
            </div>
          </div>

          <!-- AI is typing -->
          <div v-if="isTyping" class="flex items-center gap-2 text-gray-500 dark:text-gray-400 text-sm pl-4">
            <div class="flex gap-1">
              <div class="w-2 h-2 bg-amber-500 rounded-full animate-bounce" style="animation-delay: 0ms"></div>
              <div class="w-2 h-2 bg-orange-500 rounded-full animate-bounce" style="animation-delay: 150ms"></div>
              <div class="w-2 h-2 bg-rose-500 rounded-full animate-bounce" style="animation-delay: 300ms"></div>
            </div>
            <span>{{ t('thinking') }}</span>
          </div>
        </div>

        <!-- Quick prompt buttons -->
        <div class="px-4 sm:px-6 py-3 flex flex-wrap gap-2 border-t border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800">
          <button
            v-for="prompt in quickPrompts"
            :key="prompt"
            @click="usePrompt(prompt)"
            class="px-3 py-1.5 text-xs bg-gray-100 dark:bg-gray-700 hover:bg-amber-50 dark:hover:bg-amber-900/20 hover:text-amber-700 dark:hover:text-amber-400 text-gray-600 dark:text-gray-400 rounded-full transition-colors font-medium"
          >
            {{ prompt }}
          </button>
        </div>

        <!-- Input -->
        <form @submit.prevent="sendMessage" class="p-4 sm:p-5 bg-white dark:bg-gray-800 border-t border-gray-100 dark:border-gray-700 flex gap-2 sm:gap-3">
          <input
            v-model="input"
            type="text"
            :placeholder="t('askAnything')"
            class="flex-1 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg px-4 py-3 text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500 transition-all"
          />
          <button
            type="submit"
            class="px-4 py-3 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-lg font-semibold hover:shadow-lg transition-all hover:-translate-y-0.5"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"/>
            </svg>
          </button>
        </form>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, reactive, nextTick, computed } from "vue"
import { useI18n } from '@/i18n'
import { marked } from 'marked'

const { t } = useI18n()

marked.setOptions({
  breaks: true,
  gfm: true
})

const parseMarkdown = (text) => {
  return marked.parse(text)
}

const messagesContainer = ref(null)
const input = ref("")
const isTyping = ref(false)

const messages = reactive([
  {
    sender: "bot",
    text: t('chatGreeting')
  }
])

const quickPrompts = computed(() => [
  t('promptSkills'),
  t('promptExperience'),
  t('promptTech'),
])

const scrollToBottom = () => {
  nextTick(() => {
    if (messagesContainer.value) {
      messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight
    }
  })
}

const getCsrfToken = () => {
  const meta = document.querySelector('meta[name="csrf-token"]')
  return meta ? meta.getAttribute('content') : ''
}

const usePrompt = (text) => {
  input.value = text
  sendMessage()
}

const sendMessage = async () => {
  if (!input.value.trim()) return

  const userMsg = {
    sender: "user",
    text: input.value
  }

  messages.push(userMsg)
  const userText = input.value
  input.value = ""

  // Add empty bot message that we'll fill progressively
  const botMsg = reactive({ sender: "bot", text: "", isStreaming: true })
  messages.push(botMsg)
  isTyping.value = true
  scrollToBottom()

  let receivedFirst = false

  try {
    const response = await fetch('/home/stream', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': getCsrfToken(),
        'Accept': 'text/event-stream'
      },
      body: JSON.stringify({ query: userText })
    })

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`)
    }

    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''

    while (true) {
      const { done, value } = await reader.read()
      if (done) break

      buffer += decoder.decode(value, { stream: true })
      const lines = buffer.split('\n')
      buffer = lines.pop() || ''

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          const data = line.slice(6)
          if (data === '[DONE]') {
            break
          }
          try {
            const parsed = JSON.parse(data)
            if (parsed.token) {
              if (!receivedFirst) {
                receivedFirst = true
                isTyping.value = false
                console.log('[Stream] First token received')
              }
              botMsg.text += parsed.token
              scrollToBottom()
            }
          } catch (e) {
            // Skip malformed JSON
          }
        }
      }
    }
  } catch (error) {
    console.error('Stream error:', error)
    botMsg.text = t('errorOccurred')
  } finally {
    isTyping.value = false
    botMsg.isStreaming = false
  }
}
</script>
